// src/components/Dashboard/DistributionCharts.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import Sparkline from '../common/Sparkline';

const COLORS = ['#652991', '#f6b312', '#10B981', '#F59E0B', '#EF4444'];

const DistributionCharts = ({ serviceData, geographicData }) => {
  const serviceChartData = Object.entries(serviceData).map(([name, data]) => ({
    name,
    value: data.anomalies,
    impact: data.impact,
    distribution: data.distribution
  }));

  const geoChartData = Object.entries(geographicData)
    .sort((a, b) => b[1].anomalies - a[1].anomalies)
    .slice(0, 5)
    .map(([region, data]) => ({
      name: region,
      value: data.anomalies,
      impact: data.impact,
      services: data.services,
      trend: data.trend
    }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Card sx={{ p: 1 }}>
          <Typography variant="subtitle2">{data.name}</Typography>
          <Typography variant="body2">Anomalies: {data.value}</Typography>
          <Typography variant="body2">Impact: {data.impact}</Typography>
          {data.services && (
            <Box mt={1}>
              {data.services.map((service, idx) => (
                <Chip key={idx} label={service} size="small" sx={{ mr: 0.5 }} />
              ))}
            </Box>
          )}
        </Card>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Anomaly Distribution</Typography>
      <Grid container spacing={3}>
        {/* Service Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Service Distribution" />
            <CardContent>
              <Grid container>
                <Grid item xs={6}>
                  <Box height={300}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={serviceChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                        >
                          {serviceChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  {serviceChartData.map((service, index) => (
                    <Box key={index} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{service.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.value} anomalies
                        </Typography>
                      </Box>
                      {service.distribution && (
                        <Box height={30}>
                          <Sparkline 
                            data={service.distribution.map(value => ({ value }))}
                            color={COLORS[index % COLORS.length]}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Geographic Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Geographic Distribution" />
            <CardContent>
              <Grid container>
                <Grid item xs={6}>
                  <Box height={300}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={geoChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                        >
                          {geoChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  {geoChartData.map((region, index) => (
                    <Box key={index} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{region.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {region.value} anomalies
                        </Typography>
                      </Box>
                      {region.trend && (
                        <Box height={30}>
                          <Sparkline 
                            data={region.trend.map(value => ({ value }))}
                            color={COLORS[index % COLORS.length]}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DistributionCharts;